(() => {
  const cartLinks = () => Array.from(document.querySelectorAll('a[href="/cart"], a[href$="/cart"]'));

  const updateCartCount = async () => {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) return;

    const cart = await response.json();
    cartLinks().forEach((link) => {
      link.textContent = cart.item_count > 0 ? `購物車 (${cart.item_count})` : '購物車';
    });
  };

  const showStatus = (status, message, type = 'success') => {
    if (!status) return;
    status.textContent = message;
    status.hidden = !message;
    status.classList.toggle('is-error', type === 'error');
  };

  const formatCartMoney = (cents, currency = 'TWD') => {
    const amount = Number(cents || 0) / 100;
    try {
      return `${new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0
      }).format(amount)} ${currency}`;
    } catch (error) {
      return `$${Math.round(amount).toLocaleString('en-US')} ${currency}`;
    }
  };

  let productZoom;

  const closeProductZoom = () => {
    if (!productZoom) return;
    productZoom.hidden = true;
    document.body.classList.remove('has-product-zoom');
  };

  const openProductZoom = (src, alt, isContain = false) => {
    if (!src) return;

    if (!productZoom) {
      productZoom = document.createElement('div');
      productZoom.className = 'product-zoom';
      productZoom.hidden = true;
      productZoom.innerHTML = `
        <button class="product-zoom-close" type="button" aria-label="Close image preview">X</button>
        <div class="product-zoom-frame">
          <img alt="">
        </div>
      `;
      document.body.appendChild(productZoom);

      productZoom.addEventListener('click', (event) => {
        if (event.target === productZoom || event.target.closest('.product-zoom-close')) {
          closeProductZoom();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeProductZoom();
      });
    }

    const image = productZoom.querySelector('img');
    image.src = src;
    image.alt = alt || '';
    productZoom.classList.toggle('is-contain', isContain);
    productZoom.hidden = false;
    document.body.classList.add('has-product-zoom');
  };

  const setupProductGallery = () => {
    document.querySelectorAll('[data-nuvia-product-gallery]').forEach((gallery) => {
      const main = gallery.querySelector('[data-nuvia-gallery-main]');
      const slides = Array.from(gallery.querySelectorAll('[data-nuvia-gallery-slide]'));
      const thumbs = Array.from(gallery.querySelectorAll('[data-nuvia-gallery-thumb]'));

      if (!main || !slides.length) return;

      const setActive = (index, shouldScroll = false) => {
        slides.forEach((slide) => {
          slide.classList.toggle('is-active', slide.dataset.galleryIndex === index);
        });
        thumbs.forEach((thumb) => {
          thumb.classList.toggle('is-active', thumb.dataset.galleryIndex === index);
        });

        if (shouldScroll) {
          const target = slides.find((slide) => slide.dataset.galleryIndex === index);
          if (target && window.matchMedia('(max-width: 640px)').matches) {
            target.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'smooth' });
          }
        }
      };

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => setActive(thumb.dataset.galleryIndex, true));
      });

      slides.forEach((slide) => {
        slide.addEventListener('click', () => {
          const image = slide.querySelector('img');
          openProductZoom(slide.dataset.fullImage, image ? image.alt : '', slide.classList.contains('is-size-guide'));
        });
      });

      let scrollTimer;
      main.addEventListener('scroll', () => {
        if (!window.matchMedia('(max-width: 640px)').matches) return;

        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(() => {
          const closest = slides.reduce((best, slide) => {
            const distance = Math.abs(slide.offsetLeft - main.scrollLeft);
            if (!best || distance < best.distance) return { slide, distance };
            return best;
          }, null);

          if (closest && closest.slide) {
            setActive(closest.slide.dataset.galleryIndex);
          }
        }, 80);
      });
    });
  };

  const setupCartAutoUpdate = () => {
    const form = document.querySelector('[data-nuvia-cart-form]');
    if (!form) return;

    const status = form.querySelector('[data-nuvia-cart-update-status]');
    const currency = form.dataset.currency || 'TWD';
    const total = form.querySelector('[data-nuvia-cart-total]');
    const quantityInputs = Array.from(form.querySelectorAll('[data-nuvia-cart-quantity]'));

    const normalizeQuantity = (input, nextQuantity) => {
      const min = Number(input.min || 1);
      const max = Number(input.max || 9);
      const parsedQuantity = Number.parseInt(nextQuantity ?? input.value, 10);

      if (Number.isNaN(parsedQuantity)) {
        return Number(input.dataset.currentQuantity || min);
      }

      return Math.min(max, Math.max(min, parsedQuantity));
    };

    const updateCartDisplay = () => {
      let totalPrice = 0;

      form.querySelectorAll('[data-nuvia-cart-item]').forEach((row) => {
        const input = row.querySelector('[data-nuvia-cart-quantity]');
        const linePrice = row.querySelector('[data-nuvia-line-price]');
        const unitPrice = Number(row.dataset.unitPrice || 0);
        const quantity = input ? normalizeQuantity(input, input.value) : 1;
        const finalLinePrice = unitPrice * quantity;

        totalPrice += finalLinePrice;

        if (input) {
          input.value = quantity;
          input.dataset.currentQuantity = String(quantity);
        }
        if (linePrice) {
          linePrice.textContent = formatCartMoney(finalLinePrice, currency);
        }
      });

      if (total) {
        total.textContent = formatCartMoney(totalPrice, currency);
      }

      showStatus(status, '');
    };

    quantityInputs.forEach((input) => {
      input.value = normalizeQuantity(input, input.value);
      input.dataset.currentQuantity = String(input.value);

      input.addEventListener('focus', () => {
        input.select();
      });

      input.addEventListener('input', () => {
        if (input.value === '') return;
        input.value = normalizeQuantity(input, input.value);
        updateCartDisplay();
      });

      input.addEventListener('change', () => {
        const quantity = normalizeQuantity(input, input.value);

        input.value = quantity;
        input.dataset.currentQuantity = String(quantity);
        updateCartDisplay();
      });

      input.addEventListener('blur', () => {
        if (input.value !== '') return;

        input.value = input.dataset.currentQuantity || input.min || '1';
        updateCartDisplay();
      });
    });
  };

  const setupStorePickupFields = () => {
    const form = document.querySelector('[data-nuvia-cart-form]');
    if (!form) return;

    const methodSelect = form.querySelector('[data-nuvia-delivery-method]');
    const storeFields = form.querySelector('[data-nuvia-store-fields]');
    if (!methodSelect || !storeFields) return;

    const storeInputs = Array.from(storeFields.querySelectorAll('[data-nuvia-store-input]'));
    const blankInputs = Array.from(storeFields.querySelectorAll('[data-nuvia-store-blank]'));
    const chainSelect = storeFields.querySelector('[data-nuvia-store-input][name="attributes[店到店超商]"]');
    const storeNameInput = storeFields.querySelector('[data-nuvia-store-input][name="attributes[店到店門市名稱]"]');
    const storeCodeInput = storeFields.querySelector('[data-nuvia-store-input][name="attributes[店到店門市代號]"]');
    const storeAddressInput = storeFields.querySelector('[data-nuvia-store-input][name="attributes[店到店門市地址]"]');
    const pasteInput = storeFields.querySelector('[data-nuvia-store-paste]');
    const applyButton = storeFields.querySelector('[data-nuvia-store-apply]');
    const storeStatus = storeFields.querySelector('[data-nuvia-store-status]');

    const setStorePickupState = () => {
      const isStorePickup = methodSelect.value.includes('安心店到店');
      storeFields.hidden = !isStorePickup;

      storeInputs.forEach((input) => {
        input.disabled = !isStorePickup;
      });

      blankInputs.forEach((input) => {
        input.disabled = isStorePickup;
      });
    };

    const showStoreStatus = (message, type = 'success') => {
      if (!storeStatus) return;
      storeStatus.textContent = message;
      storeStatus.hidden = !message;
      storeStatus.classList.toggle('is-error', type === 'error');
    };

    const cleanStoreName = (value) => {
      return value
        .replace(/7[\s-]*ELEVEN/gi, '')
        .replace(/7[\s-]*11/gi, '')
        .replace(/全家便利商店/g, '')
        .replace(/FamilyMart/gi, '')
        .replace(/服務代號|門市代號|店號|編號|地址/g, '')
        .replace(/門市/g, '')
        .replace(/店$/g, '')
        .replace(/[：:，,。]/g, ' ')
        .trim();
    };

    const parseStoreText = (text) => {
      const normalized = String(text || '').replace(/\s+/g, ' ').trim();
      if (!normalized) return null;

      const chain = /全家|FamilyMart/i.test(normalized) ? '全家' : '7-ELEVEN';
      const codeMatch = normalized.match(/\b\d{5,6}\b/);
      const addressMatch = normalized.match(/(?:台|臺)[北中南東]|基隆市|新北市|桃園市|新竹[市縣]|苗栗縣|彰化縣|南投縣|雲林縣|嘉義[市縣]|高雄市|屏東縣|宜蘭縣|花蓮縣|澎湖縣|金門縣|連江縣/);
      const code = codeMatch ? codeMatch[0] : '';
      const address = addressMatch ? normalized.slice(addressMatch.index).trim() : '';
      let nameArea = normalized;

      if (codeMatch) {
        if (codeMatch.index === 0 && addressMatch) {
          nameArea = normalized.slice(codeMatch.index + code.length, addressMatch.index).trim();
        } else {
          nameArea = nameArea.slice(0, codeMatch.index).trim();
        }
      } else if (addressMatch) {
        nameArea = nameArea.slice(0, addressMatch.index).trim();
      }

      const name = cleanStoreName(nameArea);
      return { chain, code, address, name };
    };

    methodSelect.addEventListener('change', () => {
      setStorePickupState();
      showStoreStatus('');
    });

    storeFields.querySelectorAll('[data-nuvia-store-link]').forEach((link) => {
      link.addEventListener('click', () => {
        const chain = link.dataset.chain;
        if (chainSelect && chain) chainSelect.value = chain;
      });
    });

    if (applyButton && pasteInput) {
      applyButton.addEventListener('click', () => {
        const parsed = parseStoreText(pasteInput.value);
        if (!parsed) {
          showStoreStatus('請先貼上門市名稱、代號或地址。', 'error');
          return;
        }

        if (chainSelect) chainSelect.value = parsed.chain;
        if (storeNameInput && parsed.name) storeNameInput.value = parsed.name;
        if (storeCodeInput && parsed.code) storeCodeInput.value = parsed.code;
        if (storeAddressInput && parsed.address) storeAddressInput.value = parsed.address;

        if (!parsed.name && !parsed.code && !parsed.address) {
          showStoreStatus('沒有辨識到門市資料，請手動填下方欄位。', 'error');
          return;
        }

        showStoreStatus('已套用到下方欄位，請再確認一次門市與手機。');
      });
    }

    setStorePickupState();
  };

  const setupCartCheckoutSubmit = () => {
    const form = document.querySelector('[data-nuvia-cart-form]');
    if (!form) return;

    form.addEventListener('submit', async (event) => {
      const submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

      event.preventDefault();
      submitter.disabled = true;
      submitter.classList.add('is-loading');
      submitter.textContent = '前往結帳中...';

      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });

        if (!response.ok) throw new Error('Cart update failed');

        window.location.assign('/checkout');
      } catch (error) {
        submitter.disabled = false;
        submitter.classList.remove('is-loading');
        submitter.textContent = '前往結帳';
        showStatus(form.querySelector('[data-nuvia-cart-update-status]'), '結帳前資料儲存失敗，請再試一次。', 'error');
      }
    });
  };

  const setupHomeProductBrowser = () => {
    document.querySelectorAll('[data-nuvia-home-browser]').forEach((browser) => {
      const modeButtons = Array.from(browser.querySelectorAll('[data-nuvia-browser-mode]'));
      const panels = Array.from(browser.querySelectorAll('[data-nuvia-browser-panel]'));
      const filterButtons = Array.from(browser.querySelectorAll('[data-nuvia-browser-filter]'));
      const productCards = Array.from(browser.querySelectorAll('[data-nuvia-product-category]'));

      const setMode = (mode) => {
        browser.dataset.nuviaBrowserActiveMode = mode;

        modeButtons.forEach((button) => {
          const isActive = button.dataset.nuviaBrowserMode === mode;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        panels.forEach((panel) => {
          const isActive = panel.dataset.nuviaBrowserPanel === mode;
          panel.hidden = !isActive;
          panel.classList.toggle('is-active', isActive);
        });
      };

      const setFilter = (filter) => {
        filterButtons.forEach((button) => {
          const isActive = filter !== 'all' && button.dataset.nuviaBrowserFilter === filter;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        productCards.forEach((card) => {
          const category = card.dataset.nuviaProductCategory || '';
          card.hidden = filter !== 'all' && category !== filter;
        });
      };

      modeButtons.forEach((button) => {
        button.addEventListener('click', () => setMode(button.dataset.nuviaBrowserMode));
      });

      filterButtons.forEach((button) => {
        button.setAttribute('aria-pressed', 'false');
        button.addEventListener('click', () => {
          const filter = button.classList.contains('is-active') ? 'all' : button.dataset.nuviaBrowserFilter;
          setFilter(filter);
        });
      });

      setMode('story');
      setFilter('all');
    });
  };

  const setupProductStickyBar = () => {
    document.querySelectorAll('[data-nuvia-product-form]').forEach((form) => {
      const focusSizeButton = form.querySelector('[data-nuvia-focus-size]');
      const sizeSelector = form.querySelector('[data-nuvia-size-selector]');
      if (!focusSizeButton || !sizeSelector) return;

      focusSizeButton.addEventListener('click', () => {
        sizeSelector.scrollIntoView({ block: 'center', behavior: 'smooth' });
        window.setTimeout(() => sizeSelector.focus({ preventScroll: true }), 260);
      });
    });
  };

  document.querySelectorAll('[data-nuvia-product-form]').forEach((form) => {
    const submitButton = form.querySelector('[type="submit"]');
    const status = form.querySelector('[data-nuvia-cart-status]');
    const defaultText = submitButton ? submitButton.textContent.trim() : '';
    let resetTimer;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!submitButton || submitButton.disabled) return;

      window.clearTimeout(resetTimer);
      submitButton.disabled = true;
      submitButton.classList.add('is-loading');
      submitButton.textContent = '加入中...';
      showStatus(status, '');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });

        if (!response.ok) throw new Error('Add to cart failed');

        await updateCartCount();
        submitButton.textContent = '已加入';
        showStatus(status, '已加入購物車，可以繼續挑選或前往購物車結帳。');
      } catch (error) {
        submitButton.textContent = defaultText || '加入購物車';
        showStatus(status, '加入失敗，請再試一次。', 'error');
      } finally {
        submitButton.classList.remove('is-loading');
        submitButton.disabled = false;
        resetTimer = window.setTimeout(() => {
          submitButton.textContent = defaultText || '加入購物車';
        }, 2200);
      }
    });
  });

  setupProductGallery();
  setupHomeProductBrowser();
  setupProductStickyBar();
  setupCartAutoUpdate();
  setupStorePickupFields();
  setupCartCheckoutSubmit();
})();
